import axios from "axios";
import { getDeviceFingerprint, synchronizedTokenRefresh } from "../utils/security";
import { isLocalDevHost, resolveApiBaseUrl } from "../utils/apiBase";

// Dynamic API base URL: same-origin (dev-proxied) on localhost, configured absolute
// URL everywhere else.
function getApiBaseUrl() {
  //! #46: on a local dev origin this yields the RELATIVE "/api/v1", so calls are
  //! same-origin and the Lax refresh cookie is first-party. This MUST be decided at
  //! runtime rather than by REACT_APP_API_URL — one container serves both
  //! localhost:3000 and app.deetrade.net, and they need different answers.
  if (isLocalDevHost()) return resolveApiBaseUrl();

  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) return envUrl;

  // Not local and nothing configured — fall back to the Tailscale gateway.
  return "http://100.108.85.59:8000/api/v1";  // Fixed: Use Auth Gateway port
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 60000,
  withCredentials: true, // Enable cookies for authentication
  headers: {
    "Content-Type": "application/json",
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "1; mode=block",
  },
});

// Rate limiting configuration
const rateLimitConfig = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
};

// Request interceptor to add auth token and security headers
const addRequestHeaders = (config) => {
  const token = localStorage.getItem("accessToken"); // Changed from "access_token" to "accessToken" to match AuthContext
  const deviceInfo = getDeviceFingerprint();

  config.headers = {
    ...config.headers,
    ...(token && { Authorization: `Bearer ${token}` }),
    "X-Device-Info": JSON.stringify(deviceInfo),
    "X-Client-Version": process.env.REACT_APP_VERSION || "1.0.0",
  };

  return config;
};

// Error handling interceptor with retry logic
// Exported for testability — the interceptor's own retry/reject/opt-out logic is the unit under test (R5.1/R5.4).
export const handleResponseError = async (error, instance) => {
  const originalRequest = error.config;

  //! R5.1 — SCOPED 429 bypass. skipRateLimitRetry is set ONLY by refreshConsolidated (api.js).
  //  Reject immediately (bypasses BOTH the 3x retry below AND the flattened reject at the bottom)
  //  so the caller's `.response.status === 429` / `.response.data.error.code` checks are reachable.
  //! R9 (S2S hardening) — reject a value carrying a FRESHLY-CONSTRUCTED response with ONLY the
  //  fields the sole consumer (TradingDataContext.jsx:636-661) reads. axios aliases the request
  //  config (with `.headers.Authorization`, the bearer JWT) onto BOTH error.config AND
  //  error.response.config (settle.js), so forwarding error.response BY REFERENCE would still leak
  //  the token via caught.response.config.headers.Authorization. Rebuilding the response object
  //  drops config/request entirely — no bearer anywhere in the propagated object — while preserving
  //  .response.status/.statusText/.headers/.data (all the consumer reads). R5.4 unchanged.
  if (error.response?.status === 429 && originalRequest?.skipRateLimitRetry) {
    const r = error.response;
    return Promise.reject({
      response: { status: r.status, statusText: r.statusText, headers: r.headers, data: r.data },
      message: error.message,
      isAxiosError: true,
    });
  }

  const retryCount = originalRequest._retryCount || 0;

  // Handle rate limiting
  if (
    error.response?.status === 429 &&
    retryCount < rateLimitConfig.maxRetries
  ) {
    originalRequest._retryCount = retryCount + 1;
    const delay = Math.min(
      rateLimitConfig.initialDelay * Math.pow(2, retryCount),
      rateLimitConfig.maxDelay
    );

    await new Promise((resolve) => setTimeout(resolve, delay));
    return instance(originalRequest);
  }

  // Handle token refresh
  if (
    error.response?.status === 401 &&
    !originalRequest._retry &&
    !originalRequest.url.includes("/auth/refresh")
  ) {
    originalRequest._retry = true;
    try {
      const access_token = await synchronizedTokenRefresh(async () => {
          const response = await api.post("/auth/refresh");
          const { access_token } = response.data;
          localStorage.setItem("accessToken", access_token); 
          return access_token;
      });

      originalRequest.headers.Authorization = `Bearer ${access_token}`;

      return instance(originalRequest);
    } catch (refreshError) {
      localStorage.removeItem("accessToken"); // Changed to "accessToken"
      window.location.href = "/login";
      return Promise.reject(refreshError);
    }
  }

  // Standardize error format
  const timeoutMessage = error.code === "ECONNABORTED"
    ? "Request timed out while waiting for server response"
    : undefined;

  const _r = error.response;
  const errorResponse = {
    status: "error",
    code: error.response?.data?.code || error.code || "UNKNOWN_ERROR",
    message:
      error.response?.data?.detail ||
      error.response?.data?.message ||
      timeoutMessage ||
      error.message ||
      "An unexpected error occurred",
    timestamp: new Date().toISOString(),
    //! Preserve the upstream response (status + body) so component error handlers can inspect it.
    //! The prior flatten stripped `.response` entirely, silently DISABLING every status/code-based
    //! branch across the app — e.g. StrategyBuilder's deploy handler could never see the 409
    //! DEPLOY_DRIFT_UNRESOLVED (drift-resolution modal never opened -> user got the raw backend
    //! message as a toast) nor the 422 readiness gates. Rebuild {status,statusText,headers,data}
    //! ONLY (NOT `config`/`request`, which alias the bearer JWT via settle.js — same token-safe
    //! shape the R9 429 branch uses above), so err.response.status + err.response.data.detail are
    //! reachable again without leaking the token.
    ...(_r && {
      response: {
        status: _r.status,
        statusText: _r.statusText,
        headers: _r.headers,
        data: _r.data,
      },
    }),
  };

  return Promise.reject(errorResponse);
};

// Apply interceptors
// Apply interceptors to the single API instance
api.interceptors.request.use(addRequestHeaders);
api.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, api)
);

// All API services through the gateway
export const services = {
  models: {
    listVersions: (params = {}) => api.get("/models/versions", { params }),
    getVersion: (id) => api.get(`/models/versions/${encodeURIComponent(id)}`),
    getReferences: (id) => api.get(`/models/versions/${encodeURIComponent(id)}/references`),
    train: (body) => api.post("/models/train", body),
    transition: (id, body) => api.post(`/models/versions/${encodeURIComponent(id)}/transition`, body),
  },
  // 144-6 D-G3-1 / AC5.8 — the managed broker list a backtest can be priced on (any authenticated user).
  backtests: {
    listBrokers: () => api.get("/backtests/brokers"),
  },
  // 144-6 R4 — trade-economics schedule admin (append-only: list + append per table, nothing else).
  tradeEconomics: {
    listStatutory: (params = {}) => api.get("/admin/charges/statutory", { params }),
    appendStatutory: (body) => api.post("/admin/charges/statutory", body),
    listBroker: (params = {}) => api.get("/admin/charges/broker", { params }),
    appendBroker: (body) => api.post("/admin/charges/broker", body),
  },
  auth: {
    register: (userData) => api.post("/auth/register", userData),
    login: (credentials) => api.post("/auth/login", credentials),
    logout: () => api.post("/auth/logout"),
    refreshToken: () => api.post("/auth/refresh"),
    getProfile: () => api.get("/auth/me"),
    updateProfile: (data) => api.post("/auth/profile", data),
  },
  profile: {
    getProfile: () => api.get("/profiles/me"),
    updateProfile: (data) => api.put("/profiles/me", data),
    // Move notification methods here or create new group
    sendTestNotification: () => api.post("/notifications/test"),
  },
  brokers: {
    getBroker: (brokerId) => api.get(`/brokers/${brokerId}`),
    getAccounts: (decrypt = false) =>
      api.get(`/broker-accounts/`, { params: { decrypt } }), // user ID comes from auth token

    addBrokerAccount: (data) => {
      // Create the API payload with snake_case keys to match backend
      return api.post("/broker-accounts", {
        // Remove trailing slash for consistency
        // Required fields
        broker_id: data.broker_id,
        api_key: data.api_key,
        api_secret_key: data.api_secret_key,

        // Account identification fields
        account_id: data.account_id, // This now comes from the redirect URL response
        account_holder_name: data.account_holder_name || data.broker_user_name, // Fallback to broker_user_name

        // Additional fields from broker
        broker_user_id: data.broker_user_id || null,
        broker_user_name: data.broker_user_name || null,

        // App context
        app_name: data.app_name || "DeeTrade",
        status: data.status || "pending", // Default to pending if not provided

        // Optional fields - include only if they exist
        ...(data.settings && { settings: data.settings }),
        ...(data.client_id && { client_id: data.client_id }),
        ...(data.client_secret && { client_secret: data.client_secret }),
        ...(data.session_token && { session_token: data.session_token }),
        ...(data.session_details && { session_details: data.session_details }),
      });
    },
    updateBrokerAccount: (accountId, data) => {
      // For updates, only send fields that are explicitly provided
      const payload = {};

      // Only include fields that are defined (not null or undefined)
      if (data.api_key !== undefined) payload.api_key = data.api_key;
      if (data.api_secret_key !== undefined)
        payload.api_secret_key = data.api_secret_key;
      if (data.account_holder_name !== undefined)
        payload.account_holder_name = data.account_holder_name;
      if (data.broker_user_id !== undefined)
        payload.broker_user_id = data.broker_user_id;
      if (data.broker_user_name !== undefined)
        payload.broker_user_name = data.broker_user_name;
      if (data.status !== undefined) payload.status = data.status;
      if (data.settings !== undefined) payload.settings = data.settings;
      if (data.session_token !== undefined)
        payload.session_token = data.session_token;
      if (data.session_details !== undefined)
        payload.session_details = data.session_details;

      return api.put(`/broker-accounts/${accountId}`, payload);
    },
    deleteBrokerAccount: (accountId) =>
      api.delete(`/broker-accounts/${accountId}`),
  },
  brokerPlatforms: {
    //Will contain all the API calls related to brokers platform
    
    /**
     * Get list of all brokers with optional filtering
     * @param {Object} params - Optional parameters for filtering
     * @param {boolean} params.include_inactive - Include inactive brokers in results
     * @param {number} params.skip - Number of records to skip for pagination
     * @param {number} params.limit - Maximum number of records to return
     * @returns {Promise} - Promise resolving to list of brokers
     */
    getAllBrokers: (params = {}) => 
      api.get("/brokers/", { params }),

    /**
     * Get available (active) brokers for user
     * This is maintained for backward compatibility with existing code.
     * For new code, use getAllBrokers() instead.
     * @returns {Promise} - Promise resolving to list of active brokers
     */
    getAvailableBrokers: () => api.get("/brokers/"),

    // Get single broker by ID
    getBroker: (brokerId) => api.get(`/brokers/${brokerId}`),

    // Get brokers by country
    getBrokersByCountry: (countryCode, params = {}) =>
      api.get(`/brokers/country/${countryCode}`, { params }),

    getBrokerInstructions: (brokerId) =>
      api.get(`/brokers/${brokerId}/api-instructions/`),

    generateRedirectUrl: (brokerId, accountId = null) =>
      api.post(`/brokers/${brokerId}/redirect-url/`, {
        broker_id: brokerId,
        broker_account_id: accountId || null, // Ensure null is explicitly sent when no account ID
      }),

    // Admin operations - create, update, deactivate brokers
    createBroker: (brokerData) =>
      api.post("/brokers/", brokerData),

    updateBroker: (brokerId, brokerData) =>
      api.put(`/brokers/${brokerId}`, brokerData),

    deactivateBroker: (brokerId) =>
      api.delete(`/brokers/${brokerId}`),
    
    // Alias for clarity - DELETE endpoint is hard delete
    deleteBroker: (brokerId) => 
      api.delete(`/brokers/${brokerId}`),
  },
  // New accounts services for the Trade Accounts module
  brokerAccounts: {

    /* -------------------------------------------------------------------------------------------------------------------------
    * Endpoints for broker accounts
    * These endpoints are used for retrieving / updating broker accounts in DeeTrade 
    * ------------------------------------------------------------------------------------------------------------------------- */
    // Get all user's trade accounts - used
    getAccounts: () => api.get(`/broker-accounts/`), // user ID comes from auth token

    // Get specific account details - used
    getAccount: (accountId) => api.get(`/broker-accounts/${accountId}`),

    getDecryptedCredentials: (accountId) =>
      api.get(`/broker-accounts/${accountId}/credentials`),

    // Create new trade account - used
    createAccount: (data) => {
      const payload = {
        broker_id: data.broker_id,
        account_id: data.account_id, // This should come from the redirect URL response
        account_holder_name: data.account_holder_name, // Use account_holder_name to match the backend
        app_name: data.app_name, // Added app_name
        api_key: data.api_key,
        api_secret_key: data.api_secret, // Changed from api_secret to api_secret_key
        // totp_secret and additional_params are removed as they are not in BrokerAccountCreate
        // If they are needed for specific brokers, this might require a different approach
        // or backend schema updates. For now, aligning with BrokerAccountCreate.
        // You might also want to explicitly send optional fields if they exist in `data`
        // and are part of the BrokerAccountCreate schema, e.g., status, broker_user_id
      };
      return api.post("/broker-accounts/", payload);
    },

    // Update existing trade account -used
    updateAccount: (accountId, data) => {
      const payload = {};

      // Only include fields that are defined
      if (data.account_holder_name !== undefined)
        payload.account_holder_name = data.account_holder_name;
      if (data.api_key !== undefined) payload.api_key = data.api_key;
      if (data.api_secret !== undefined)
        payload.api_secret_key = data.api_secret;
      if (data.totp_secret !== undefined)
        payload.totp_secret = data.totp_secret;
      if (data.additional_params !== undefined)
        payload.additional_params = data.additional_params;
      if (data.app_name !== undefined) payload.app_name = data.app_name;

      return api.put(`/broker-accounts/${accountId}`, payload);
    },
    // Get login URL for a broker account - used
    getLoginUrl: (accountId, isReconnect = false) =>
      api.get(`/broker-accounts/${accountId}/login-url/`, {
        broker_account_id: accountId,
        is_reconnect: isReconnect,
      }),

    // Submit session details for broker authentication - used for login callback processing
    submitSessionDetails: (accountId, sessionDetails) => {
      const payload = {
        broker_account_id: accountId,
        session_details: {
          // URL parameters from broker redirect
          ...(sessionDetails.paramDetails && { paramDetails: sessionDetails.paramDetails }),
          
          // Device fingerprint for security
          ...(sessionDetails.device_fingerprint && { device_fingerprint: sessionDetails.device_fingerprint }),
          
          // User identification
          ...(sessionDetails.user_id && { user_id: sessionDetails.user_id }),
          
          // Broker account identification
          ...(sessionDetails.broker_account_id && { broker_account_id: sessionDetails.broker_account_id }),
          
          // Any additional session data
          ...sessionDetails
        }
      };

      return api.post(`/broker-sessions/${accountId}/session-details`, payload);
    },

    // Validate broker account
    validateAccount: (accountId) =>
      api.get(`/broker-accounts/${accountId}/validate`, { accountId }),

    // Delete trade account
    deleteAccount: (accountId) => api.delete(`/broker-accounts/${accountId}`),
    
    // Get API rate limit usage for broker account
    getRateLimitUsage: (accountId) =>
      api.get(`/broker-accounts/${accountId}/rate-limit-usage`),

    // order-instrument-normalization R5.3/R6.1.1 — class-keyed broker capability manifest for
    // this account (gateway -> BIS ownership-or-superuser relay -> adapter GET /capabilities,
    // S2S). Path is `/brokers/{account}/capabilities`, NOT `/broker-accounts/...` — mirrors the
    // gateway's own registration (route_manager.py:1239-1246, modelled on the existing
    // `/orders/{order_id}/modify-capabilities` descriptor route). Grouped here (not under
    // brokerPlatforms) because it is account-scoped, not broker-platform-scoped — same shape as
    // getRateLimitUsage above. NEVER call the adapter/BIS host:port directly — gateway only.
    getBrokerCapabilities: (accountId) =>
      api.get(`/brokers/${accountId}/capabilities`),

    /* -------------------------------------------------------------------------------------------------------------------------
    * Additional endpoints for broker accounts
    * These endpoints are used for retrieving additional information by calling the broker accounts API
    * ------------------------------------------------------------------------------------------------------------------------- */

    // Get fund/balance details for broker account
    getFunds: (accountId) =>
      api.get(`/broker-accounts/${accountId}/funds`),

    // Get customer details for broker account
    getCustomerDetails: (accountId) =>
      api.get(`/broker-accounts/${accountId}/customer-details`),

    // Get previous day close prices for instruments (used for watchlist color coding)
    // Payload: { instruments: [{ instrument_id, exchange, symbol, broker_token?, product_type?, expiry_date?, strike_price?, right? }] }
    // Returns: { data: { prices: { "NSE:RELIANCE:EQ": 2850.50, ... }, source, cached_count, fetched_count } }
    getPreviousClose: (accountId, instruments) =>
      api.post(`/broker-accounts/${accountId}/market-info/previous-close`, { instruments }),
  },

  /* -------------------------------------------------------------------------------------------------------------------------
  * Instrument Master Service endpoints
  * Admin-only endpoints for managing instrument data, broker mappings, and sync operations
  * All routes require 'admin' scope and go through Auth Gateway at /api/v1/instruments/*
  * ------------------------------------------------------------------------------------------------------------------------- */
  instruments: {
    // Search instruments with filters (user scope)
    searchInstruments: (params) =>
      api.get("/instruments/search", { params }),

    // Browse instruments with pagination and filtering (user scope)
    browseInstruments: (params) =>
      api.get("/instruments/browse", { params }),

    // Get instrument details by ID (user scope)
    getInstrument: (instrumentId) =>
      api.get(`/instruments/instruments/${instrumentId}`),

    // Get broker mappings for a specific instrument (user scope)
    // Public gateway path: /api/v1/mappings/instrument/{id}
    getInstrumentMappings: (instrumentId) =>
      api.get(`/mappings/instrument/${instrumentId}`),

    // Service health check (user scope)
    getHealth: () =>
      api.get("/instruments/health"),

    // Admin endpoints (require 'admin' scope)
    admin: {
      // Trigger instrument sync from source (source as query parameter)
      triggerSync: (source, params = {}) =>
        api.post(`/instruments/sync/trigger`, { ...params }, { params: { source } }),

      // Get sync status by sync_id
      getSyncStatus: (syncId) =>
        api.get(`/instruments/sync/status/${syncId}`),

      // Get sync progress details
      getSyncProgress: (syncId) =>
        api.get(`/instruments/sync/progress/${syncId}`),

      // Get sync history with pagination
      getSyncHistory: (params) =>
        api.get("/instruments/sync/history", { params }),

      // Mark stale syncs as failed (cleanup orphaned syncs)
      markStaleSyncs: (staleMinutes = 15) =>
        api.post("/instruments/sync/history/mark-stale", null, { params: { stale_minutes: staleMinutes } }),

      // Get latest sync for a source
      getLatestSync: (source) =>
        api.get(`/instruments/sync/latest/${source}`),

      // Get broker sync status
      getBrokerSyncStatus: (brokerId) =>
        api.get(`/instruments/broker-status/${brokerId}`),

      // Get sync logs
      getSyncLogs: (params) =>
        api.get("/instruments/admin/sync-logs", { params }),

      // Get sync statistics
      getSyncStats: () =>
        api.get("/instruments/admin/stats"),

      // Get service configuration
      getServiceConfig: () =>
        api.get("/instruments/admin/config"),

      // Update broker mapping
      updateBrokerMapping: (mappingId, data) =>
        api.put(`/instruments/admin/mappings/${mappingId}`, data),
      
      // Scheduled Tasks Management (times returned in UTC, convert to local for display)
      // List all scheduled sync tasks
      getScheduledTasks: () =>
        api.get("/instruments/schedules"),

      // Get specific scheduled task details
      getScheduledTask: (taskId) =>
        api.get(`/instruments/schedules/${taskId}`),

      // Update scheduled task configuration (times should be in UTC)
      updateScheduledTask: (taskId, data) =>
        api.put(`/instruments/schedules/${taskId}`, data),

      // Create a new dynamic broker schedule
      createDynamicSchedule: (data) =>
        api.post(`/instruments/schedules/dynamic`, data),

      // Delete an existing broker schedule
      deleteScheduledTask: (taskId) =>
        api.delete(`/instruments/schedules/${taskId}`),

      // Restart Celery Beat scheduler to apply updated schedules
      restartSchedules: () =>
        api.post("/instruments/schedules/restart"),

      // Get server time in UTC for timezone synchronization
      getServerTime: () =>
        api.get("/instruments/schedules/server-time/current"),

      /* -------------------------------------------------------------------------------------------------------------------------
      * Broker Priority Configuration endpoints
      * Admin-only endpoints for managing broker data source priority rankings
      * ------------------------------------------------------------------------------------------------------------------------- */
      // Get all priority rules
      getPriorityRules: (params) =>
        api.get("/instruments/priority-rules", { params }),

      // Get priority matrix (all rules organized by exchange and instrument type)
      getPriorityMatrix: () =>
        api.get("/instruments/priority-rules/matrix"),

      // Get priority for specific instrument
      getPriorityForInstrument: (exchange, instrumentType, segment = null) =>
        api.get("/instruments/priority-rules/for-instrument", {
          params: { exchange, instrument_type: instrumentType, segment }
        }),

      // Create new priority rule
      createPriorityRule: (data) =>
        api.post("/instruments/priority-rules", data),

      // Update priority rule
      updatePriorityRule: (priorityId, data) =>
        api.put(`/instruments/priority-rules/${priorityId}`, data),

      // Delete priority rule
      deletePriorityRule: (priorityId) =>
        api.delete(`/instruments/priority-rules/${priorityId}`),

      // Bulk update priority rankings
      bulkUpdatePriorities: (updates) =>
        api.post("/instruments/priority-rules/bulk", { updates }),

      /* -------------------------------------------------------------------------------------------------------------------------
      * Reconciliation & Conflict Resolution endpoints
      * Admin-only endpoints for viewing and resolving instrument data conflicts
      * ------------------------------------------------------------------------------------------------------------------------- */
      // Get conflicts list with filters
      getConflicts: (params) =>
        api.get("/instruments/conflicts", { params }),

      // Get conflict detail for specific instrument
      getConflictDetail: (instrumentId) =>
        api.get(`/instruments/conflicts/${instrumentId}`),

      // Resolve conflict manually
      resolveConflict: (instrumentId, data) =>
        api.post(`/instruments/conflicts/${instrumentId}/resolve`, data),

      // Change primary broker for instrument
      changePrimaryBroker: (instrumentId, data) =>
        api.post(`/instruments/conflicts/${instrumentId}/primary-broker`, data),

      // Get reconciliation statistics
      getReconciliationStats: () =>
        api.get("/instruments/reconciliation/stats"),

      // Get validation failures
      getValidationFailures: (params) =>
        api.get("/instruments/conflicts/validation-failures", { params }),

      // Bulk resolve conflicts
      bulkResolveConflicts: (resolutions) =>
        api.post("/instruments/conflicts/bulk-resolve", { resolutions }),
    },
  },

  /* -------------------------------------------------------------------------------------------------------------------------
  * User Role Management endpoints (Superuser-only)
  * Admin endpoints for managing user roles and access control (RBAC)
  * All routes require is_superuser=True (checked via get_current_superuser dependency)
  * ------------------------------------------------------------------------------------------------------------------------- */
  users: {
    // List all users (Admin only)
    listUsers: (skip = 0, limit = 100) =>
      api.get("/users/", { params: { skip, limit } }),

    // Get user details by ID (Admin only)
    getUser: (userId) =>
      api.get(`/users/${userId}`),

    // Update user role (Admin only)
    updateUserRole: (userId, role) =>
      api.put(`/users/${userId}/role`, { role }),
      
    // Create internal user (Admin only)
    createInternalUser: (userData) =>
      api.post("/users/internal", userData),

    // Update user tier (Admin only)
    updateUserTier: (userId, tierName) =>
      api.put(`/users/${userId}/tier`, { tier_name: tierName }),
  },

  /* -------------------------------------------------------------------------------------------------------------------------
  * Dynamic Role Management endpoints (Superuser-only)
  * CRUD operations for managing roles - allows superusers to create custom roles
  * All routes require is_superuser=True (checked via get_current_superuser dependency)
  * ------------------------------------------------------------------------------------------------------------------------- */
  roles: {
    // List all roles with user count (Superuser only)
    listRoles: (skip = 0, limit = 100, includeInactive = false) =>
      api.get("/roles/", { params: { skip, limit, include_inactive: includeInactive } }),

    // Get role details by ID (Superuser only)
    getRole: (roleId) =>
      api.get(`/roles/${roleId}`),

    // Create a new role (Superuser only)
    createRole: (roleData) =>
      api.post("/roles/", roleData),

    // Update an existing role (Superuser only)
    updateRole: (roleId, roleData) =>
      api.put(`/roles/${roleId}`, roleData),

    // Delete a role (Superuser only)
    deleteRole: (roleId) =>
      api.delete(`/roles/${roleId}`),

    // Seed default system roles (Superuser only)
    seedDefaultRoles: () =>
      api.post("/roles/seed"),
  },

  // Market Data Management Service (MDMS)
  // All MDMS requests go through the API Gateway for authentication and security
  marketData: {
    // Request historical data download for an instrument
    // Routes through Auth Gateway -> MDMS backend
    // Payload: { instrument: { instrument_id, symbol, exchange }, start_date, end_date, user: { id, email }, options: { continuous } }
    requestHistorical: (payload) =>
      api.post("/historical/request", payload),
    
    // List all instruments with historical data
    // Returns: { total, instruments: [{ instrument_id, symbol, exchange, first_data_time, last_data_time, total_records }] }
    listInstruments: (params = {}) =>
      api.get("/historical/instruments", { params }),
    
    // Get detailed information about a specific instrument
    // Returns: { instrument_id, symbol, exchange, first_data_time, last_data_time, total_records, ... }
    getInstrumentDetails: (instrumentId) =>
      api.get(`/historical/instruments/${instrumentId}`),
    
    // Get yearly calendar view of data availability
    // Returns: { year, instrument_id, calendar: { month: { date: count } } }
    // instrumentMetadata: Optional { instrument_type, symbol, exchange, expiry_date, strike_price, option_type }
    getYearlyCalendar: (instrumentId, year, timeframe = '1d', instrumentMetadata = null) => {
      const params = { timeframe };
      
      // Add instrument metadata if provided (enables continuous aggregation for empty instruments)
      if (instrumentMetadata) {
        if (instrumentMetadata.instrument_type) params.instrument_type = instrumentMetadata.instrument_type;
        if (instrumentMetadata.symbol) params.symbol = instrumentMetadata.symbol;
        if (instrumentMetadata.exchange) params.exchange = instrumentMetadata.exchange;
        if (instrumentMetadata.expiry_date || instrumentMetadata.expiry) {
          params.expiry_date = instrumentMetadata.expiry_date || instrumentMetadata.expiry;
        }
        if (instrumentMetadata.strike_price || instrumentMetadata.strike) {
          params.strike_price = instrumentMetadata.strike_price || instrumentMetadata.strike;
        }
        if (instrumentMetadata.option_type) params.option_type = instrumentMetadata.option_type;
      }
      
      return api.get(`/historical/availability/${instrumentId}/year/${year}`, { params });
    },
    
    // Get OHLC data for a specific date range
    // Returns: { instrument_id, start_date, end_date, timeframe, data: [{ time, open, high, low, close, volume }] }
    // instrumentMetadata: Optional { instrument_type, symbol, exchange, expiry_date, strike_price, option_type }
    getDateRangeData: (instrumentId, params, instrumentMetadata = null, config = {}) => {
      const mergedParams = { ...params };

      // Add instrument metadata if provided (enables continuous aggregation for empty instruments)
      if (instrumentMetadata) {
        if (instrumentMetadata.instrument_type) mergedParams.instrument_type = instrumentMetadata.instrument_type;
        if (instrumentMetadata.symbol) mergedParams.symbol = instrumentMetadata.symbol;
        if (instrumentMetadata.exchange) mergedParams.exchange = instrumentMetadata.exchange;
        if (instrumentMetadata.expiry_date || instrumentMetadata.expiry) {
          mergedParams.expiry_date = instrumentMetadata.expiry_date || instrumentMetadata.expiry;
        }
        if (instrumentMetadata.strike_price || instrumentMetadata.strike) {
          mergedParams.strike_price = instrumentMetadata.strike_price || instrumentMetadata.strike;
        }
        if (instrumentMetadata.option_type) mergedParams.option_type = instrumentMetadata.option_type;
        if (instrumentMetadata.rollover_days !== undefined) mergedParams.rollover_days = instrumentMetadata.rollover_days;
      }

      return api.get(`/historical/data/${instrumentId}`, { params: mergedParams, ...config });
    },
    
    // Get data gaps for quality analysis
    // Returns: { gaps: [{ start_time, end_time, expected_records, missing_records }] }
    getDataGaps: (instrumentId, params) =>
      api.get(`/historical/gaps/${instrumentId}`, { params }),
    
    // Insert OHLC data (admin only)
    // Payload: { instrument_id, symbol, exchange, data: [{ time, open, high, low, close, volume }] }
    insertOHLCData: (payload) =>
      api.post("/historical/data/insert", payload),
    
    // Get broker options for historical data download
    // Returns: { available_brokers: [{broker_id, broker_name}], recommended_broker_id, has_multiple_options }
    getBrokerOptions: (instrumentId) =>
      api.get(`/historical/broker-options/${instrumentId}`),
    
    // Get rollover defaults for exchanges (for continuous contract mode)
    // Returns all defaults: { defaults: {exchange: days}, exchange_details: [...] }
    getRolloverDefaults: () =>
      api.get("/rollover-settings/defaults"),

    // Get specific exchange's rollover default
    // Returns: { exchange, default_days, is_default_fallback }
    getRolloverDefault: (exchangeCode) =>
      api.get(`/rollover-settings/defaults/${exchangeCode}`),

    // Get paginated OHLC data for lazy loading
    // Payload: { instrument_id, timeframe, start_date, end_date, page, page_size_days }
    // Returns: { status, data: [...], pagination: {...}, request_metadata: {...} }
    getPagedOHLC: (payload, config = {}) =>
      api.post(`/historical/data/${payload.instrument_id}/range/paginated`, {
        instrument_id: payload.instrument_id,
        timeframe: payload.timeframe || '5m',
        start_date: payload.start_date,
        end_date: payload.end_date,
        page: payload.page || 1,
        page_size_days: payload.page_size_days || 10,
        // Instrument metadata for rollover stitching (futures contracts)
        instrument_type: payload.instrument_type || null,
        symbol: payload.symbol || null,
        exchange: payload.exchange || null,
        expiry_date: payload.expiry_date || null,
        rollover_days: payload.rollover_days ?? null,
      }, config),
  },

  // Subscription Tiers (Admin)
  tiers: {
    list: () => api.get("/subscriptions/tiers"),
    get: (id) => api.get(`/subscriptions/tiers/${id}`),
    create: (data) => api.post("/subscriptions/tiers", data),
    update: (id, data) => api.put(`/subscriptions/tiers/${id}`, data),
    delete: (id) => api.delete(`/subscriptions/tiers/${id}`),
  },

  // Technical Indicators Service
  indicators: {
    // List all available indicators
    list: () => api.get("/indicators/"),

    // Calculate indicator values
    // Payload: { instrument_id, timeframe, start_date, end_date, indicator: { type, params } }
    // Options: { signal } - AbortController signal for request cancellation
    calculate: (payload, options = {}) => api.post("/indicators/calculate", payload, { timeout: 180000, ...options }),
  },

  // Real-time Feeds Management
  // Manages subscriptions to real-time data feeds stored in MDMS
  realtimeFeeds: {
    // List all real-time feeds with optional filters
    // Returns: { total, feeds: [{ id, symbol, exchange, instrument_type, status, ... }] }
    list: (params = {}) =>
      api.get("/realtime-feeds", { params }),
    
    // Create a new real-time feed subscription
    // Payload: { instrument_id, symbol, exchange, instrument_type, is_continuous, rollover_days_before_expiry, active_expiry_date }
    create: (payload) =>
      api.post("/realtime-feeds", payload),
    
    // Get details of a specific feed
    get: (feedId) =>
      api.get(`/realtime-feeds/${feedId}`),
    
    // Update feed settings (capture toggles, status, rollover days)
    update: (feedId, payload) =>
      api.put(`/realtime-feeds/${feedId}`, payload),
    
    // Delete a feed subscription
    delete: (feedId) =>
      api.delete(`/realtime-feeds/${feedId}`),
    
    // Toggle tick data capture
    toggleTick: (feedId) =>
      api.post(`/realtime-feeds/${feedId}/toggle-tick`),
    
    // Toggle minute data capture
    toggleMinute: (feedId) =>
      api.post(`/realtime-feeds/${feedId}/toggle-minute`),
    
    // Toggle HQ (multi-broker) mode
    toggleHQ: (feedId) =>
      api.post(`/realtime-feeds/${feedId}/toggle-hq`),
  },

  // Exchange Status Service
  // Real-time market status validation and configuration
  exchangeStatus: {
    // Get current status for all exchanges
    getAllStatus: () =>
      api.get("/exchange-status/all"),
    
    // Get status for specific exchange
    getExchangeStatus: (exchangeCode) =>
      api.get(`/exchange-status/${exchangeCode}`),

    // isStreamingReady deleted (#31/exchange-session-ssot WP-6, Decommission #17):
    // dead — the paired gateway route (auth/app/core/config.py:451-458) 404s and
    // had no caller anywhere in the frontend.

    // Admin Settings APIs
    settings: {
      // Get complete settings overview
      getOverview: () =>
        api.get("/exchange-status/settings/overview"),
      
      // Get all exchange configurations
      getAllConfigs: () =>
        api.get("/exchange-status/settings/exchanges"),
      
      // Get specific exchange config
      getExchangeConfig: (exchangeCode) =>
        api.get(`/exchange-status/settings/exchanges/${exchangeCode}`),
      
      // Update exchange validation config
      updateExchangeConfig: (exchangeCode, data) =>
        api.put(`/exchange-status/settings/exchanges/${exchangeCode}`, data),
      
      // Toggle exchange enable/disable
      toggleExchange: (exchangeCode, enabled) =>
        api.post(`/exchange-status/settings/exchanges/${exchangeCode}/toggle`, null, {
          params: { enabled }
        }),
      
      // Get all API credentials (keys masked)
      getAPICredentials: () =>
        api.get("/exchange-status/settings/api-credentials"),
      
      // Update API credential
      updateAPICredential: (data) =>
        api.put("/exchange-status/settings/api-credentials", data),
      
      // Get decrypted API key (admin only - use with caution)
      getDecryptedAPIKey: (provider) =>
        api.get(`/exchange-status/settings/api-credentials/${provider}/decrypt`),
      
      // Get audit log
      getAuditLog: (exchangeCode = null, limit = 50) =>
        api.get("/exchange-status/settings/audit-log", {
          params: { exchange_code: exchangeCode, limit }
        }),
    },
  },

  // Streaming Orchestrator Service
  // Real-time market data streaming control plane
  streaming: {
    // Get current streaming orchestrator status
    // Returns: { status, running, is_leader, demand, exchange_status, stats }
    getStatus: () =>
      api.get("/streaming/status"),
    
    // Get current streaming demand matrix
    // Returns: { demand, exchange_status, total_instruments, streaming_enabled }
    getDemand: () =>
      api.get("/streaming/demand"),
    
    // Get detailed per-instrument demand matrix for UI table
    // Returns: { instruments: [...], stats: {...}, exchange_status, orchestrator_status }
    getDemandMatrix: () =>
      api.get("/streaming/demand-matrix"),
    
    // Start streaming for instruments (registers demand)
    // Payload: { instrument_ids: [], broker?: string }
    start: (payload) =>
      api.post("/streaming/start", payload),
    
    // Stop streaming for instruments (decrements demand)
    // Payload: { instrument_ids: [] }
    stop: (payload) =>
      api.post("/streaming/stop", payload),
    
    // Force immediate reconciliation (admin)
    forceReconcile: () =>
      api.post("/streaming/force-reconcile"),
    
    // Force start streaming for specific tokens (admin/debug)
    forceStart: (broker, tokens) =>
      api.post("/streaming/force-start", null, { params: { broker, tokens } }),
    
    // Force stop streaming for specific tokens (admin/debug)
    forceStop: (broker, tokens) =>
      api.post("/streaming/force-stop", null, { params: { broker, tokens } }),
  },

  // MDMS Broker Priority Configuration
  // Manages fusion engine broker priority for price, metadata, and depth
  mdms: {
    // Get current broker priority configuration
    // Returns: { price: [...], metadata: [...], depth: [...] }
    getBrokerPriority: () =>
      api.get("/config/broker-priority"),
    
    // Update priority order for a specific type (price, metadata, or depth)
    // Payload: { brokers: ["flattrade", "icici", ...] }
    updateBrokerPriority: (priorityType, payload) =>
      api.put(`/config/broker-priority/${priorityType}`, payload),
    
    // Add a new broker to all priority types (Legacy/Bulk)
    addBroker: (brokerName) =>
      api.post("/config/broker-priority/broker", null, { params: { broker_name: brokerName } }),
    
    // Remove a broker from all priority types (Legacy/Bulk)
    removeBroker: (brokerName) =>
      api.delete(`/config/broker-priority/broker/${brokerName}`),
      
    // Add broker to specific exchange and priority type (New Atomic)
    addBrokerToType: (exchange, priorityType, brokerName) =>
      api.post(`/config/broker-priority/${exchange}/${priorityType}/broker`, { broker_name: brokerName }),
      
    // Remove broker from specific exchange and priority type (New Atomic)
    removeBrokerFromType: (exchange, priorityType, brokerName) =>
      api.delete(`/config/broker-priority/${exchange}/${priorityType}/broker/${brokerName}`),
    
    // Get detailed priority configuration with enabled status
    getDetailedPriority: () =>
      api.get("/config/broker-priority/detailed"),
  },


  // Instrument Master Service
  instrumentMaster: {
    // Search instruments by symbol or name (gateway route: /api/v1/instruments/search)
    search: (params = {}) =>
      api.get("/instruments/search", { params }),

    getProfile: (symbol, exchange) =>
      api.get("/instruments/profile", { params: { symbol, exchange } }),

    // Get instrument details by ID (gateway route: /api/v1/instruments/{id})
    getInstrument: (instrumentId) =>
      api.get(`/instruments/${instrumentId}`),

    // Bulk-fetch instruments by UUID or EXCHANGE:SYMBOL composite key (gateway route:
    // POST /api/v1/instruments/bulk, registered backend/auth/app/core/config.py — C-R1.2).
    // NOTE: instrument-master returns a BARE JSON array (response_model=List[InstrumentResponse]),
    // NOT the {status/data/error} envelope — mirrors instrument_validity_resolver.py:129-131.
    // res.data is the array itself; callers should read res.data[0] for a single-id lookup.
    getInstrumentsBulk: (instrumentIds) =>
      api.post(`/instruments/bulk`, { instrument_ids: instrumentIds }),

    // Get broker mappings for an instrument (gateway route: /api/v1/mappings/instrument/{id})
    getMappings: (instrumentId) =>
      api.get(`/mappings/instrument/${instrumentId}`),

    // List all instruments with filters (gateway route: /api/v1/instruments)
    listInstruments: (params = {}) =>
      api.get("/instruments", { params }),
    
    // Get calculated expiry dates for F&O instruments (gateway route: /api/v1/instruments/expiry-calendar)
    // Returns: { symbol, exchange, year, pattern: { description, day_of_week }, expiry_dates: ["YYYY-MM-DD"] }
    getExpiryCalendar: async (params) => {
      try {
        const response = await api.get("/instruments/expiry-calendar", { params });
        return response.data;
      } catch (error) {
        console.warn('Failed to fetch expiry calendar:', error);
        return { expiry_dates: [], pattern: null };
      }
    },

    // Get the current near-month active contract for a continuous instrument
    // Gateway route: /api/v1/instruments/active-contract
    //! broker-qty-semantics iter-7 — optional instrumentType (4th arg) mirrors the readiness gate's
    //  resolve_active_contract call (it always passes instrument_type). Omitted → endpoint defaults
    //  FUTURE (unchanged for existing 3-arg callers, e.g. WatchList). Passing OPTION for a continuous
    //  OPTION strategy keeps the FE display in lockstep with the gate (which would otherwise diverge).
    getActiveContract: (identifier, exchange, rolloverDays = 0, instrumentType) =>
      api.get("/instruments/active-contract", { params: {
        base_symbol: identifier,
        exchange,
        rollover_days: rolloverDays,
        ...(instrumentType ? { instrument_type: instrumentType } : {}),
      } }),

    //! broker-qty-semantics loop-back iter-6 — getActiveContractById REMOVED (decommissioned).
    //  It was added in iter-4 solely to power a UUID-path stub→active-contract re-resolution FOR
    //  DISPLAY on the Step-4 OrderCard. iter-6 removed that redirect (it diverged from the
    //  authoritative readiness gate on expired contracts, confusing users); the OrderCard now shows
    //  the persisted row's real lot. That was this endpoint's only caller, so it is dead code and is
    //  deleted per the no-dead-code rule. (base_symbol variant getActiveContract above is unaffected.)

    // Get rollover schedule regarding current and next contract
    // Gateway route: /api/v1/instruments/rollover-schedule
    getRolloverSchedule: (identifier, exchange) =>
      api.get("/instruments/rollover-schedule", { params: { base_symbol: identifier, exchange } }),

    // Get the full chain of contracts for a base symbol
    // Gateway route: /api/v1/instruments/contract-chain
    getContractChain: (identifier, exchange) =>
      api.get("/instruments/contract-chain", { params: { base_symbol: identifier, exchange } }),

    // Get broker-specific trading symbols for instrument IDs
    // Gateway route: /api/v1/instruments/trading-symbols
    // Used to fetch broker_symbol and broker_token for Previous Close API calls
    getTradingSymbols: (instrumentIds, brokerName = null) =>
      api.post("/instruments/trading-symbols", {
        instrument_ids: instrumentIds,
        broker_name: brokerName
      }),
  },
  
  // =============================================================================
  // JOB ORCHESTRATOR - Centralized Job Scheduling & Execution
  // =============================================================================
  jobOrchestrator: {
    // Get dashboard with stats, upcoming jobs, and recent failures
    getDashboard: () => api.get("/jobs/dashboard"),
    
    // List all scheduled jobs
    listJobs: (params = {}) => api.get("/jobs", { params }),
    
    // Get specific job
    getJob: (jobId) => api.get(`/jobs/${jobId}`),
    
    // Create new job
    createJob: (data) => api.post("/jobs", data),
    
    // Update job
    updateJob: (jobId, data) => api.put(`/jobs/${jobId}`, data),
    
    // Delete job
    deleteJob: (jobId) => api.delete(`/jobs/${jobId}`),
    
    // Manually trigger a job
    triggerJob: (jobId, options = {}) => 
      api.post(`/jobs/${jobId}/trigger`, options),
    
    // Enable a job
    enableJob: (jobId) => api.post(`/jobs/${jobId}/enable`),
    
    // Disable a job
    disableJob: (jobId) => api.post(`/jobs/${jobId}/disable`),
    
    // Get execution history for a job
    getJobRuns: (jobId, params = {}) => 
      api.get(`/jobs/${jobId}/runs`, { params }),
    
    // List all job runs (global history)
    listAllRuns: (params = {}) => api.get("/runs", { params }),
    
    // Get detailed run information
    getRun: (runId) => api.get(`/runs/${runId}`),
  },

  // =============================================================================
  // USER WATCHLISTS - Persistent watchlist storage with items
  // =============================================================================
  watchlist: {
    // Get all watchlists with items for current user
    // Returns: [{ id, name, display_order, items: [...] }]
    getAll: () => api.get("/watchlists"),
    
    // Create new watchlist
    // Payload: { name: string, display_order?: number }
    create: (data) => api.post("/watchlists", data),
    
    // Update watchlist (rename, reorder)
    update: (watchlistId, data) => api.put(`/watchlists/${watchlistId}`, data),
    
    // Delete watchlist and all its items
    delete: (watchlistId) => api.delete(`/watchlists/${watchlistId}`),
    
    // Add instrument to watchlist
    // Payload: { instrument_id, symbol, exchange, instrument_type, name?, strike_price?, option_type?, expiry_date?, is_continuous?, rollover_days_before_expiry?, active_expiry_date? }
    addItem: (watchlistId, itemData) => 
      api.post(`/watchlists/${watchlistId}/items`, itemData),
    
    // Remove instrument from watchlist
    removeItem: (watchlistId, itemId) => 
      api.delete(`/watchlists/${watchlistId}/items/${itemId}`),
    
    // Reorder items within watchlist
    // Payload: { item_orders: [{ item_id: number, display_order: number }] }
    reorderItems: (watchlistId, itemOrders) => 
      api.put(`/watchlists/${watchlistId}/items/reorder`, { item_orders: itemOrders }),
  },

  // =============================================================================
  // SIMULATION - Real-time historical data replay for testing
  // =============================================================================
  simulation: {
    // List all simulations for current user
    // Returns: { total, simulations: [{ id, name, instrument_id, status, ... }] }
    list: (params = {}) => api.get("/simulations", { params }),
    
    // Create new simulation config
    // Payload: { name, instrument_id, symbol, exchange, start_date, end_date, timeframe, delay_ms }
    create: (data) => api.post("/simulations", data),

    // Pre-create alignment guardrail for continuous sims (warn-only)
    // Returns payload directly: { aligned: boolean, mismatches: [{source, id, name, rollover_days}, ...] }
    checkAlignment: async ({ base_symbol, exchange, instrument_type, rollover_days }) => {
      const params = new URLSearchParams({ base_symbol, exchange, instrument_type });
      if (rollover_days !== null && rollover_days !== undefined) {
        params.set('rollover_days', String(rollover_days));
      }
      const res = await api.get(`/simulations/check-alignment?${params.toString()}`);
      return res.data; // {aligned, mismatches}
    },

    // Get simulation details
    get: (simulationId) => api.get(`/simulations/${simulationId}`),
    
    // Update simulation config (name, delay)
    update: (simulationId, data) => api.put(`/simulations/${simulationId}`, data),
    
    // Delete simulation
    delete: (simulationId) => api.delete(`/simulations/${simulationId}`),
    
    // Start simulation replay (progress updates via WebSocket)
    start: (simulationId) => api.post(`/simulations/${simulationId}/start`),
    
    // Stop running simulation
    stop: (simulationId) => api.post(`/simulations/${simulationId}/stop`),

    // Pause running simulation
    pause: (simulationId) => api.post(`/simulations/${simulationId}/pause`),
  },
  
  // =============================================================================
  // VOLUME SETTINGS - Manage broker specific volume format settings
  // =============================================================================
  volumeSettings: {
    // List all volume settings with optional filters (broker_code, exchange)
    list: (params = {}) => api.get("/volume-settings", { params }),
    
    // Create or update (upsert) setting - mainly for aggregator (but can be used for manual creation)
    upsert: (data) => api.post("/volume-settings", data),
    
    // Manually override a setting (sets auto_detected=false, manually_overridden=true)
    override: (id, data) => api.put(`/volume-settings/${id}/override`, data),
    
    // Delete a setting
    delete: (id) => api.delete(`/volume-settings/${id}`),
  },
  // =============================================================================
  // PLACEHOLDERS: STRATEGIES & TRADING (To be implemented in backend)
  // =============================================================================
  strategies: {
    // Get strategy by ID
    getStrategy: async (id) => {
       console.log(`MOCK API: getStrategy(${id})`);
       return { data: {} };
    },
    // Get strategy config for a symbol
    getStrategyConfig: async (symbol) => {
       console.log(`MOCK API: getStrategyConfig(${symbol})`);
       return { data: {} };
    },

    // 10509-15 R2/R5/R6 — ONE shared order-pricing authority for both pricing screens (this
    // modal's old 3-field allocations PUT, and Step-4's per-node picker). Gateway-routed GET/PUT,
    // same ETag idiom as the allocations PUT (LiveDeploymentSettingsModal.jsx:355-359) — mandatory
    // `If-Match` on the write, never a direct microservice call.
    // Response envelope: { status, data: { pricing: [{ account_id, entry_reference_type,
    // entry_bid_ask_side, entry_price_offset, exit_reference_type, pricing_source,
    // defaults_filled, ... }], defaults: { entry_reference_type, entry_bid_ask_side,
    // entry_price_offset, exit_reference_type }, etag }, error }.
    getStrategyPricing: (strategyId) => api.get(`/strategies/${strategyId}/pricing`),

    // `rows` — array of { account_id, entry_reference_type, entry_bid_ask_side,
    // entry_price_offset, exit_reference_type }. 409 (PRICING_STALE) on etag mismatch (reload and
    // retry, never silently retried with a stale payload — same discipline as the allocations
    // PUT's 409); 422 on invalid values (surface the error message verbatim).
    putStrategyPricing: (strategyId, rows, etag) =>
      api.put(`/strategies/${strategyId}/pricing`, { pricing: rows }, { headers: { 'If-Match': etag } }),
  },

  notifications: {
    subscribe: (payload) => api.post("/notifications/subscribe", payload),
    unsubscribe: (payload) => api.post("/notifications/unsubscribe", payload),
  },

  trading: {
    // Place a new order
    placeOrder: async (payload) => api.post("/orders/place", payload),
    // Get orders with optional filters
    getOrders: async (filters = {}) => {
       console.log("MOCK API: getOrders", filters);
       return { data: [] };
    },
    // Cancel an order
    cancelOrder: async (orderId) => {
       console.log(`MOCK API: cancelOrder(${orderId})`);
       return { data: { success: true } };
    },

    // C4 P3 — Repointed to the consolidated read view (GET /orders/consolidated).
    // The old OMS endpoints /orders/live and /orders/live/reconcile were removed in C1.
    // All traffic goes through the gateway at auth:8000.

    // Canonical consolidated view — params: { execution_mode, account_id, from_dt, to_dt, books }
    // Response: { status, data: { as_of, orders: [...], positions: [...], trades: [...] }, error }
    getConsolidatedView: (params = {}) => api.get("/orders/consolidated", { params }),

    // 10494-13 change 24 — v1 Holdings tab. Gateway-routed GET, no direct microservice call.
    // Response: { status, data: { as_of, holdings: [...], accounts: [...], degraded }, error }
    getHoldings: (params = {}) => api.get("/holdings", { params }),

    // Thin alias — existing callers of getLiveOrders continue to work unchanged.
    // P4 may migrate call sites to getConsolidatedView; do not break this name.
    getLiveOrders: (params = {}) => api.get("/orders/consolidated", { params }),

    // R114 — trigger an on-demand broker REST sweep (POST), then callers GET the view.
    // account_id is forwarded as a query param (gateway → order-view). Body is null (params only).
    // Returns 202 on accepted, 429 if rate-limited (caller should still proceed to GET).
    //! R5.1 — skipRateLimitRetry opts THIS call out of the shared interceptor's 429 retry+flatten so
    //  the caller sees the 429 immediately with .response intact (Reconcile-now visible feedback).
    refreshConsolidated: (params = {}) => api.post("/orders/consolidated/refresh", null, { params, skipRateLimitRetry: true }),

    // DEPRECATED: bare GET alias kept for legacy call sites. Do NOT use for sweep-triggering.
    // TradingDataContext.runReconcile now calls refreshConsolidated (POST) first, then getConsolidatedView (GET).
    postReconcile: (body = {}) => api.get("/orders/consolidated", { params: body || {} }),

    // #102 R22 — triggers the REAL RiskReconciliationJob (distinct subsystem from the
    // broker_orders sweep above). Gateway-routed, standard user JWT (same `api` client —
    // no service key). Server-side debounced + tenant-guarded (403 if the caller doesn't
    // own account_id); callers must not let a non-2xx here break the existing refresh flow.
    reconcileNow: (accountId) => api.post("/orders/reconcile-now", { account_id: accountId }),

    // 104 D9 — GET /orders/rejected through the gateway. Called with NO `strategy_instance_id`
    // param (user-scoped mode): returns ALL of the caller's rejections (algo + manual) across
    // every strategy_instance_id, tenant-scoped server-side by the JWT→X-User-Id. The existing
    // per-sii mode is unchanged — pass { strategy_instance_id } to scope to one sii.
    // Response: { status, data: { rejected_signals: [...], oms_degraded: bool }, error }
    getRejectedSignals: (params = {}) => api.get("/orders/rejected", { params }),
  },

  // =============================================================================
  // SELF-SERVICE RECOVERY — 104 Element R (D67/R12, self-service primary, admin backup)
  // =============================================================================
  // Gateway-registered routes (backend/auth/app/gateway/route_manager.py:1364-1421,
  // confirmed against the landed backend package d28d044e). The gateway forwards the
  // caller's verified JWT identity as X-User-ID to the OMS-internal handler
  // (self_service_recovery.py), which applies its OWN tenant fence BEFORE dispatch —
  // never rely on a UI-only restriction (R12.2's own AC). Every call here goes through
  // the gateway (auth:8000), never a direct order-management host:port.
  //
  // Response envelope for all four: { status, data: <ForceCloseResponse|ManualRepairResponse>, error }.
  // Errors: 400 (acknowledge_loss missing/false), 404 (not this caller's position — the SAME
  // code for "doesn't exist" and "belongs to someone else", per the non-disclosure convention),
  // 409/503 forwarded from the underlying dispatch.
  // #133 (2026-08-28) — TOP-LEVEL key (services.killSwitch — NOT under trading, which closes above).
  // The caller's OWN user-scope kill-switch. Tripped automatically by the
  // R57 cascade when a live entry fills unprotected; sticky until a human resumes. Identity is
  // the JWT (gateway injects X-User-ID) — no scope_value from the client, ever.
  killSwitch: {
    // GET /api/v1/kill-switch/user/self -> { scope, scope_value, active, last_audit }
    getOwn: () => api.get('/kill-switch/user/self'),
    // POST /api/v1/kill-switch/user/self/clear  Body: { reason }
    clearOwn: (reason) => api.post('/kill-switch/user/self/clear', { reason }),
  },

  selfServiceRecovery: {
    // R12.1/R12.2/R12.4 — user force-closes their OWN stuck/escalated position.
    // POST /api/v1/positions/{sii}/force-close
    // Body: { lifecycle_id, reason, compliance_note, acknowledge_loss }
    forceClose: (sii, payload) => api.post(`/positions/${sii}/force-close`, payload),

    // L2-WP5/F-CONS-5/F-L3-2 — batch variant so PositionsTable issues ONE request for all
    // non-FLAT rows instead of one per row. Same admission fan-out server-side
    // (self_service_recovery.py) as the single-sii GET this replaced; same
    // recoverable=null-is-disabled contract.
    //! F-3-11 (WP3/WP4): the single-sii `GET /positions/{sii}/recovery-status` wrapper
    //! (`getRecoveryStatus`) and its gateway route were DELETED — this batch method was
    //! already the sole production caller (grep-verified: only test stubs referenced the
    //! single-sii wrapper, asserting it was NOT called). Do not re-add a per-row GET; extend
    //! this batch method instead.
    // `rows` is an array of `{ sii, instrumentId, accountId }` — ONE entry per position row.
    //! The WIRE carries the deduped `sii` list ONLY. The batch endpoint
    //! (`self_service_recovery.py::self_service_recovery_status_batch`) declares exactly one
    //! query param, `sii: List[str] = Query(...)`; it does NOT accept or zip `instrument_id`/
    //! `account_id`. It returns EVERY row this caller owns under those siis, each keyed at full
    //! row grain (C-1: `f"{sii}|{instrument_id}|{account_id or ''}"`) — so the row grain comes
    //! back on the RESPONSE key, it is not something the request has to express. The FE builds
    //! its Map with the byte-identical expression (PositionsTable.jsx) and looks up only the
    //! rows it rendered; rows it did not ask about are simply never read. Sending per-row
    //! instrument_id/account_id arrays would be silently dropped by FastAPI (undeclared params)
    //! AND would triple the query-string length against the 200-sii cap — do not add them back
    //! without first adding the matching server-side params + tests.
    // GET /api/v1/positions/recovery-status?sii=a&sii=b
    // Response: { statuses: { [rowKey]: { recoverable, arm, execution_mode, reason? } } }
    //! axios's default paramsSerializer encodes array params as `sii[]=a&sii[]=b` (bracket
    //! form, verified against axios@1.9.0 via `AxiosInstance.getUri`), which FastAPI's
    //! `Query(...)` does NOT parse as repeated values. Force the plain repeated-key form here.
    getRecoveryStatusBatch: (rows) =>
      api.get('/positions/recovery-status', {
        params: {
          sii: [...new Set(rows.map((r) => r.sii))],
        },
        paramsSerializer: {
          indexes: null, // sii=a&sii=b, not sii[]=a&sii[]=b or sii[0]=a
        },
      }),

    // R12.1/R12.2/R12.4 — user repairs books-only netting for their OWN manual position
    // (sii must be in the `manual:` namespace — enforced server-side).
    // POST /api/v1/manual-positions/{sii}/repair-netting
    // Body: { instrument_id, account_id, idempotency_key, compliance_note, true_exit_price?, acknowledge_loss }
    repairNetting: (sii, payload) => api.post(`/manual-positions/${sii}/repair-netting`, payload),

    // R12.3 — admin backup routes (scopes=["admin"] at the gateway). Same payload shapes as
    // the user-facing pair above, minus the tenant-fence 404 (the admin route's own
    // SII-derived owner_user_id check applies instead — admin_force_close.py:245-261).
    // POST /api/v1/admin/positions/{sii}/force-close
    adminForceClose: (sii, payload) => api.post(`/admin/positions/${sii}/force-close`, payload),

    // POST /api/v1/admin/manual-positions/{sii}/repair-netting
    adminRepairNetting: (sii, payload) => api.post(`/admin/manual-positions/${sii}/repair-netting`, payload),
  },

  // =============================================================================
  // BACKTESTING ENGINE
  // =============================================================================
  backtesting: {
    // Run a new backtest
    // Payload: { strategy_id, start_time, end_time, initial_capital, slippage, broker? } (144-6: no commission input - charges come from the managed schedule)
    run: (payload) => api.post("/strategies/backtest/run", payload),
    
    // Check status of a specific backtest run
    getStatus: (runId) => api.get(`/strategies/backtest/status/${runId}`),
    
    // Get detailed results of a completed backtest
    getResults: (runId) => api.get(`/strategies/backtest/results/${runId}`),
    
    // List historical backtest runs (optionally filtered by strategyId)
    listRuns: (params = {}) => api.get("/strategies/backtest/runs", { params }),
  },

  // =============================================================================
  // LIVE EXIT LANE — R7.1/R7.2 (docs/pipelines/2026-07-24-live-exit-lane/02-design.md)
  // =============================================================================
  liveExitLane: {
    // R7.1 — per-broker-account protection-mechanism transparency (§1 R7.1, AMENDED).
    // GET /protection-routing?strategy_id=<uuid> — CONFIRMED against the landed sibling backend
    // package (backend/order-management/app/api/v1/endpoints/protection_routing.py +
    // backend/auth/app/gateway/route_manager.py, both found mid-implementation of this package,
    // 2026-07-24). Path is `/protection-routing`, NOT `/order-management/protection-routing` —
    // the design's prose name was descriptive shorthand ("the route order-management owns"), not
    // a literal URL segment; order-management's other gateway routes (/orders, /positions, ...)
    // omit a service-name prefix too (route_manager.py's `service` field carries the routing
    // decision, not the path) and this endpoint follows that same convention.
    // Response envelope: { status, data: { strategy_id, rows: [{ account_id, broker, mechanism,
    // label }] }, error } — label is EXACTLY "synthesized by DeeTrade — behaves like bracket/OCO"
    // (synthetic) or "native bracket/cover/OCO — managed by broker" (native).
    getProtectionRouting: (strategyId) =>
      api.get("/protection-routing", { params: { strategy_id: strategyId } }),

    // R7.2 — protection/chase/re-arm/eod_close_owner settings, written to
    // StrategyDeployment.policy_overrides (§1 R7.2, AMENDED).
    // CONFIRMED against the landed backend package (backend/strategy-management/app/api/v1/
    // endpoints/deployments.py::update_deployment_policy + backend/auth/app/gateway/
    // route_manager.py, both registered under commit 56f55783 — this was landed by a sibling
    // package concurrently with the frontend package that originally guessed a different,
    // unconfirmed route/field-name shape; corrected here during Tranche-B integration).
    // Route: PATCH /api/v1/strategies/{strategy_id}/deployments/{deployment_id}/policy (gateway-
    // registered, public_path == internal_path). MERGE semantics — an omitted (undefined/null)
    // field leaves the CURRENT stored value alone, never zeroed.
    // Body fields (exact JSON keys, DeploymentPolicySettingsRequest): chase_n (int, 1-20),
    // chase_interval_s (float, >0-300), chase_cap_pct (float, >0-1, fraction not percent),
    // profit_lock_threshold_pct (float, >0-1, fraction not percent), eod_close_owner
    // ("platform_first" | "broker_rms" — D41: stored values unchanged, only the label shown to
    // the user and the default for BRAND-NEW deployments changed; #118 WP6), exit_lead_minutes
    // (int, >=1, WP6 — platform-mode pre-cutoff exit lead in minutes; only meaningful under
    // eod_close_owner='platform_first').
    // Response envelope: { status, data: { deployment_id, strategy_id, policy_overrides }, error }.
    updateDeploymentSettings: (strategyId, deploymentId, payload) =>
      api.patch(`/strategies/${strategyId}/deployments/${deploymentId}/policy`, payload),

    // R7.3 — exit-lane notification verbosity ('verbose' | 'act_only'), user-scoped (NOT
    // per-strategy/per-deployment). CONFIRMED against the landed backend endpoint
    // (backend/user-management/app/api/v1/endpoints/trading_settings.py::get/set_notification_
    // rules + gateway route_manager.py, Tranche-B integration fix — no sibling package had
    // built this route when W6-FRONTEND originally shipped VerbosityToggleRow as local-only
    // state; wired here). Response envelope for both:
    // { status, data: { user_id, exit_lane_verbosity, is_custom }, error }.
    getNotificationVerbosity: () =>
      api.get("/trading-settings/notification-rules"),
    setNotificationVerbosity: (exitLaneVerbosity) =>
      api.put("/trading-settings/notification-rules", { exit_lane_verbosity: exitLaneVerbosity }),
  },
};

export default api;
